import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { functionsBaseUrl } from "../config/runtime";
import "../v2/workflows.css";

export default function VerifyApprovedListingPayment() {
    const location = useLocation();
    const navigate = useNavigate();
    const reference = new URLSearchParams(location.search).get("reference") || new URLSearchParams(location.search).get("trxref");
    const [retry, setRetry] = useState(0);
    const [state, setState] = useState({ status: "checking", message: "Confirming your payment with Paystack…" });

    useEffect(() => {
        let current = true;
        let timer;
        let requestTimer;
        let controller;
        if (!reference) {
            setState({ status: "error", message: "We could not find a payment reference. Use the secure payment link from your approval email." });
            return () => { current = false; };
        }
        setState({ status: "checking", message: "Confirming your payment with Paystack…" });
        // Bound the whole check as well as each request, including a stalled response body.
        const deadline = window.setTimeout(() => {
            current = false;
            window.clearTimeout(timer);
            window.clearTimeout(requestTimer);
            controller?.abort();
            setState({ status: "delayed", message: "Confirmation is taking longer than expected. Your listing will stay private until your subscription is verified. You can check again without making another payment." });
        }, 60000);
        const verify = async () => {
            controller = new AbortController();
            requestTimer = window.setTimeout(() => controller.abort(), 12000);
            try {
                const response = await fetch(`${functionsBaseUrl}/verifyApprovedListingPayment?reference=${encodeURIComponent(reference)}`, { signal: controller.signal });
                const result = await response.json();
                window.clearTimeout(requestTimer);
                if (!current) return;
                if (response.ok && result.success && result.status === "approved") {
                    window.clearTimeout(deadline);
                    if (result.access?.canPublishListing === true && result.pending !== true) {
                        setState({ status: "success", message: "Payment confirmed. Your listing is live and your subscription is active. Taking you to sign in…" });
                        timer = window.setTimeout(() => navigate("/shop-login?subscription=active", { replace: true }), 2500);
                    } else {
                        setState({
                            status: "attention",
                            message: "Your payment reference is linked to an approved account, but the account has no current verified paid period. Your listing remains private. Sign in to review billing or contact support; checking here will not charge you again.",
                        });
                    }
                    return;
                }
                if (response.status === 202 && result.pending) {
                    setState({ status: "checking", message: "Payment received. We’re waiting for Paystack to confirm your subscription. Please don’t pay again." });
                    timer = window.setTimeout(verify, 5000);
                    return;
                }
                window.clearTimeout(deadline);
                setState({ status: "error", message: result.error || "We could not confirm this payment. If Paystack charged you, contact Signet Print with your payment reference." });
            } catch {
                window.clearTimeout(requestTimer);
                if (!current) return;
                window.clearTimeout(deadline);
                setState({ status: "delayed", message: "We couldn’t reach the payment verifier. Check again when your connection is restored. Please don’t make another payment." });
            }
        };
        verify();
        return () => {
            current = false;
            window.clearTimeout(timer);
            window.clearTimeout(requestTimer);
            window.clearTimeout(deadline);
            controller?.abort();
        };
    }, [reference, retry, navigate]);

    return <main className="v2-auth-shell v2-auth-main">
        <Helmet><title>Confirm listing payment - Signet Print</title></Helmet>
        <section className="v2-auth-card v2-auth-form">
            <p className="v2-flow-kicker">Signet Print</p>
            <div role="status" aria-live="polite" aria-atomic="true">
                {state.status === "checking" && <span className="v2-payment-spinner" aria-hidden="true" />}
                <h1>{state.status === "success" ? "You’re live" : state.status === "checking" ? "Confirming payment" : state.status === "delayed" ? "Still waiting for confirmation" : "Payment needs attention"}</h1>
                <p>{state.message}</p>
            </div>
            {reference && <p className="v2-payment-reference">Payment reference: <strong>{reference}</strong></p>}
            {state.status !== "checking" && state.status !== "success" && reference && <button className="v2-button v2-button-fill" onClick={() => setRetry(value => value + 1)}>Check payment again</button>}
            {state.status !== "checking" && <Link className="v2-button v2-button-fill" to={state.status === "success" ? "/shop-login?subscription=active" : "/shop-login"}>{state.status === "success" ? "Sign in to your dashboard" : state.status === "attention" ? "Sign in to manage billing" : "Return to sign in"}</Link>}
            {(state.status === "delayed" || state.status === "error" || state.status === "attention") && <Link className="v2-button" to="/contact">Contact support</Link>}
        </section>
    </main>;
}
